import React, {useEffect, useState} from 'react';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import {Button, Card, Collapse, Grid, Image, Space, Spin, Tag, Timeline, Typography} from 'antd';
import {CameraOutlined, FireOutlined, LinkOutlined, StarOutlined, UserOutlined} from '@ant-design/icons';
import dayjs from 'dayjs';
import NewsService from '../../service/newsService';
import {ClusterSummaryView, ClusterTimelineView} from '../../types/news';
import {decodeHtmlEntities} from '../../utils/htmlUtils';
import '../../styles/NewsDetail.css';

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const ClusterDetail: React.FC = () => {
    const { clusterId } = useParams<{ clusterId: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const screens = useBreakpoint();
    const [summary, setSummary] = useState<ClusterSummaryView | null>(null);
    const [timeline, setTimeline] = useState<ClusterTimelineView | null>(null);
    const [similarClusters, setSimilarClusters] = useState<ClusterSummaryView[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [imageError, setImageError] = useState(false);

    useEffect(() => {
        const fetchClusterData = async () => {
            if (!clusterId) return;
            setLoading(true);
            try {
                const [summaryResponse, timelineResponse, similarResponse] = await Promise.all([
                    NewsService.getClusterSummary(Number(clusterId)),
                    NewsService.getClusterTimeline(Number(clusterId)),
                    NewsService.getSimilarClusters(Number(clusterId), 8).catch(() => ({ data: [] }))
                ]);

                const summaryData = summaryResponse.data;

                // Single-article cluster — redirect to source URL
                if (summaryData.num_related_articles <= 1 && summaryData.article_url) {
                    window.location.replace(summaryData.article_url);
                    return;
                }

                setSummary(summaryData);
                setTimeline(timelineResponse.data);
                setSimilarClusters(similarResponse.data);
            } catch (err: any) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };
        fetchClusterData();
    }, [clusterId]);

    const handleBack = () => {
        // Check if we have state information about where we came from
        const fromPath = location.state?.from;
        
        if (fromPath) {
            // Navigate back to the specific page we came from
            navigate(fromPath);
        } else {
            // Fallback: try to go back in history, then to home
            try {
                navigate(-1);
            } catch {
                navigate('/');
            }
        }
    };

    if (loading) {
        return <Spin spinning style={{ display: 'block', textAlign: 'center', margin: '50px 0' }} />;
    }

    if (error || !summary || !timeline) {
        return (
            <div style={{ padding: '24px', maxWidth: '800px', margin: '0 auto' }}>
                <Text type="danger">{error || 'Cluster not found'}</Text>
                <Button onClick={handleBack} style={{ marginLeft: '16px' }}>
                    Back to List
                </Button>
            </div>
        );
    }

    return (
        <div style={{ padding: '24px', maxWidth: '1000px', margin: '0 auto' }}>
            <Card>
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <Space align="center">
                        <Title 
                            level={2} 
                            style={{ 
                                margin: 0,
                                color: summary.root_breaking_rating && summary.root_breaking_rating >= 4 ? '#cf1322' : undefined
                            }}
                        >
                            {summary.root_breaking_headline || summary.cluster_headline}
                        </Title>
                        {summary.root_breaking_rating && summary.root_breaking_rating >= 4 && (
                            <Tag 
                                color="red" 
                                icon={<FireOutlined />}
                                style={{ 
                                    fontSize: '14px',
                                    padding: '4px 12px',
                                    fontWeight: 'bold'
                                }}
                            >
                                BREAKING NEWS
                            </Tag>
                        )}
                    </Space>
                    
                    {/* Breaking news rating and updates */}
                    {(summary.root_breaking_rating || summary.last_breaking_rating) && (
                        <Space>
                            {summary.root_breaking_rating && summary.root_breaking_rating >= 4 && (
                                <Tag 
                                    color="red" 
                                    icon={<StarOutlined />}
                                    style={{ fontSize: '12px' }}
                                >
                                    Rating: {summary.root_breaking_rating}/5
                                </Tag>
                            )}
                            {summary.last_breaking_rating && summary.last_breaking_rating !== summary.root_breaking_rating && (
                                <Tag color="orange">
                                    Updated: {summary.last_breaking_rating}/5
                                </Tag>
                            )}
                            {summary.last_breaking_update_datetime && (
                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                    Last breaking update: {dayjs(summary.last_breaking_update_datetime).fromNow()}
                                </Text>
                            )}
                        </Space>
                    )}
                </Space>
                
                {summary.cluster_image && !imageError && (
                    <div style={{ marginBottom: '24px' }}>
                        <Image
                            src={summary.cluster_image}
                            alt="Cluster Cover"
                            style={{ 
                                width: '100%', 
                                maxHeight: '400px', 
                                objectFit: 'cover',
                                borderRadius: '8px'
                            }}
                            fallback="https://via.placeholder.com/800x400"
                            onError={() => setImageError(true)}
                        />
                        {/* Cluster Image Attribution */}
                        {summary.root_image_source_url && (
                            <div style={{ 
                                marginTop: '8px', 
                                padding: '12px', 
                                backgroundColor: '#f8f9fa', 
                                borderRadius: '6px',
                                border: '1px solid #e9ecef'
                            }}>
                                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                                    <Space size="small" align="center">
                                        <Tag color="blue" icon={<CameraOutlined />} style={{ fontSize: '12px', fontWeight: '500' }}>
                                            Photo Source
                                        </Tag>
                                    </Space>
                                    
                                    {summary.root_image_source_author && (
                                        <Space size="small" align="center">
                                            <UserOutlined style={{ color: '#666', fontSize: '12px' }} />
                                            <Text type="secondary" style={{ fontSize: '12px', fontWeight: '500' }}>
                                                Photo by {summary.root_image_source_author}
                                            </Text>
                                            {summary.root_image_source_author_url && (
                                                <a 
                                                    href={summary.root_image_source_author_url} 
                                                    target="_blank" 
                                                    rel="noopener noreferrer"
                                                    style={{ 
                                                        fontSize: '12px',
                                                        color: '#1890ff',
                                                        textDecoration: 'none'
                                                    }}
                                                >
                                                    <LinkOutlined /> View Profile
                                                </a>
                                            )}
                                        </Space>
                                    )}
                                    
                                    <Space size="small" align="center">
                                        <a 
                                            href={summary.root_image_source_url} 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            style={{ 
                                                fontSize: '12px',
                                                color: '#1890ff',
                                                textDecoration: 'none',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '4px'
                                            }}
                                        >
                                            <LinkOutlined />
                                            View Original
                                        </a>
                                    </Space>
                                </Space>
                            </div>
                        )}
                    </div>
                )}

                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                    <div>
                        <Text type="secondary">
                            First reported by {summary.website_name} • {dayjs(summary.root_pubdate).format('MMM D, YYYY')}
                        </Text>
                        <br />
                        <Text type="secondary">
                            Last update: {dayjs(summary.last_article_pubdate).fromNow()}
                        </Text>
                        <br />
                        <Text strong>{summary.num_related_articles} related articles</Text>
                    </div>

                    <div 
                        className="article-content"
                        dangerouslySetInnerHTML={{ 
                            __html: decodeHtmlEntities(summary.cluster_summary)
                        }}
                    />

                    {timeline.articles.length > 0 && (
                        <Collapse
                            defaultActiveKey={[]}
                            style={{
                                width: '100%',
                                marginTop: '16px'
                            }}
                            items={[{
                                key: 'timeline',
                                label: (
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        width: '100%'
                                    }}>
                                        <span style={{
                                            fontSize: '16px',
                                            fontWeight: 'bold',
                                            color: '#1890ff'
                                        }}>
                                            Story Timeline ({timeline.articles.length} articles)
                                        </span>
                                    </div>
                                ),
                            children: (
                                <div style={{ 
                                    width: '100%',
                                    padding: '0'
                                }}>
                                    {screens.md ? (
                                        // Desktop: Use traditional timeline with left labels
                                        <Timeline
                                            mode="left"
                                            style={{ 
                                                width: '100%'
                                            }}
                                            items={timeline.articles.map(article => ({
                                                label: dayjs(article.publication_datetime).format('MMM D, YYYY HH:mm'),
                                                children: (
                                                    <Card 
                                                        size="small" 
                                                        hoverable
                                                        onClick={() => {
                                                            if (article.article_url) {
                                                                window.open(article.article_url, '_blank', 'noopener,noreferrer');
                                                            } else {
                                                                navigate(`/news/${article.article_id}`, {
                                                                    state: { from: location.pathname }
                                                                });
                                                            }
                                                        }}
                                                        style={{
                                                            width: '100%'
                                                        }}
                                                    >
                                                        <Space direction="vertical" size="small" style={{ width: '100%' }}>
                                                            <Space align="center" wrap>
                                                                <Title 
                                                                    level={5} 
                                                                    style={{ 
                                                                        margin: 0,
                                                                        color: article.breaking_rating && article.breaking_rating >= 4 ? '#cf1322' : undefined,
                                                                        fontSize: '16px',
                                                                        lineHeight: '1.4'
                                                                    }}
                                                                >
                                                                    {article.breaking_headline || article.article_headline}
                                                                </Title>
                                                                {article.breaking_rating && article.breaking_rating >= 4 && (
                                                                    <Tag 
                                                                        color="red" 
                                                                        icon={<FireOutlined />}
                                                                        style={{ 
                                                                            fontSize: '10px',
                                                                            padding: '2px 6px',
                                                                            fontWeight: 'bold'
                                                                        }}
                                                                    >
                                                                        BREAKING
                                                                    </Tag>
                                                                )}
                                                            </Space>
                                                            <Space wrap>
                                                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                                                    {article.website_name}
                                                                </Text>
                                                                {article.breaking_rating && article.breaking_rating >= 4 && (
                                                                    <Tag 
                                                                        color="red" 
                                                                        icon={<StarOutlined />}
                                                                        style={{ fontSize: '10px' }}
                                                                    >
                                                                        {article.breaking_rating}/5
                                                                    </Tag>
                                                                )}
                                                            </Space>
                                                        </Space>
                                                        {article.article_summary && (
                                                            <div 
                                                                className="article-content"
                                                                dangerouslySetInnerHTML={{ 
                                                                    __html: decodeHtmlEntities(article.article_summary)
                                                                }}
                                                                style={{ 
                                                                    marginTop: '8px',
                                                                    fontSize: '14px',
                                                                    lineHeight: '1.5'
                                                                }}
                                                            />
                                                        )}
                                                    </Card>
                                                ),
                                            }))}
                                        />
                                    ) : (
                                        // Mobile: Use vertical timeline with full-width cards
                                        <Timeline
                                            mode="left"
                                            style={{ 
                                                width: '100%',
                                                margin: '0',
                                                padding: '0'
                                            }}
                                            items={timeline.articles.map(article => ({
                                                dot: <div style={{ 
                                                    width: '8px', 
                                                    height: '8px', 
                                                    borderRadius: '50%', 
                                                    backgroundColor: '#1890ff',
                                                    border: '2px solid #fff',
                                                    boxShadow: '0 0 0 2px #1890ff'
                                                }} />,
                                                children: (
                                                    <div style={{ 
                                                        width: '100%',
                                                        marginLeft: '-8px'
                                                    }}>
                                                        <Card
                                                            size="small"
                                                            hoverable
                                                            onClick={() => {
                                                                if (article.article_url) {
                                                                    window.open(article.article_url, '_blank', 'noopener,noreferrer');
                                                                } else {
                                                                    navigate(`/news/${article.article_id}`, {
                                                                        state: { from: location.pathname }
                                                                    });
                                                                }
                                                            }}
                                                            style={{
                                                                width: '100%',
                                                                marginBottom: '16px'
                                                            }}
                                                        >
                                                            <Space direction="vertical" size="small" style={{ width: '100%' }}>
                                                                <div style={{ 
                                                                    display: 'flex', 
                                                                    justifyContent: 'space-between', 
                                                                    alignItems: 'flex-start',
                                                                    marginBottom: '8px'
                                                                }}>
                                                                    <Text type="secondary" style={{ 
                                                                        fontSize: '11px',
                                                                        fontWeight: '500'
                                                                    }}>
                                                                        {dayjs(article.publication_datetime).format('MMM D, YYYY • HH:mm')}
                                                                    </Text>
                                                                    {article.breaking_rating && article.breaking_rating >= 4 && (
                                                                        <Tag 
                                                                            color="red" 
                                                                            icon={<FireOutlined />}
                                                                            style={{ 
                                                                                fontSize: '9px',
                                                                                padding: '1px 4px',
                                                                                fontWeight: 'bold',
                                                                                lineHeight: '1.2'
                                                                            }}
                                                                        >
                                                                            BREAKING
                                                                        </Tag>
                                                                    )}
                                                                </div>
                                                                
                                                                <Title 
                                                                    level={5} 
                                                                    style={{ 
                                                                        margin: 0,
                                                                        color: article.breaking_rating && article.breaking_rating >= 4 ? '#cf1322' : undefined,
                                                                        fontSize: '15px',
                                                                        lineHeight: '1.3',
                                                                        fontWeight: '600'
                                                                    }}
                                                                >
                                                                    {article.breaking_headline || article.article_headline}
                                                                </Title>
                                                                
                                                                <div style={{ 
                                                                    display: 'flex', 
                                                                    justifyContent: 'space-between', 
                                                                    alignItems: 'center',
                                                                    flexWrap: 'wrap',
                                                                    gap: '8px'
                                                                }}>
                                                                    <Text type="secondary" style={{ fontSize: '12px' }}>
                                                                        {article.website_name}
                                                                    </Text>
                                                                    {article.breaking_rating && article.breaking_rating >= 4 && (
                                                                        <Tag 
                                                                            color="red" 
                                                                            icon={<StarOutlined />}
                                                                            style={{ fontSize: '10px' }}
                                                                        >
                                                                            {article.breaking_rating}/5
                                                                        </Tag>
                                                                    )}
                                                                </div>
                                                            </Space>
                                                            {article.article_summary && (
                                                                <div 
                                                                    className="article-content"
                                                                    dangerouslySetInnerHTML={{ 
                                                                        __html: decodeHtmlEntities(article.article_summary)
                                                                    }}
                                                                    style={{ 
                                                                        marginTop: '12px',
                                                                        fontSize: '14px',
                                                                        lineHeight: '1.5'
                                                                    }}
                                                                />
                                                            )}
                                                        </Card>
                                                    </div>
                                                ),
                                            }))}
                                        />
                                    )}
                                </div>
                            )
                        }]}
                        />
                    )}

                    {/* Similar Clusters Section */}
                    {similarClusters.length > 0 && (
                        <Card
                            title={
                                <Space>
                                    <span style={{ fontSize: '18px', fontWeight: 'bold' }}>
                                        Related News
                                    </span>
                                    <Tag color="blue">{similarClusters.length}</Tag>
                                </Space>
                            }
                            style={{ marginTop: '24px' }}
                        >
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: screens.md ? 'repeat(2, 1fr)' : '1fr',
                                gap: '16px'
                            }}>
                                {similarClusters.map((cluster) => (
                                    <Card
                                        key={cluster.cluster_id}
                                        hoverable
                                        size="small"
                                        onClick={() => {
                                            if (cluster.num_related_articles <= 1 && cluster.article_url) {
                                                window.open(cluster.article_url, '_blank', 'noopener,noreferrer');
                                            } else {
                                                window.scrollTo({ top: 0, behavior: 'smooth' });
                                                navigate(`/cluster/${cluster.cluster_id}`);
                                            }
                                        }}
                                        style={{ cursor: 'pointer' }}
                                        cover={
                                            cluster.cluster_image ? (
                                                <Image
                                                    src={cluster.cluster_image}
                                                    alt={cluster.cluster_headline}
                                                    style={{
                                                        height: '150px',
                                                        objectFit: 'cover'
                                                    }}
                                                    fallback="https://via.placeholder.com/300x150"
                                                    preview={false}
                                                />
                                            ) : undefined
                                        }
                                    >
                                        <Space direction="vertical" size="small" style={{ width: '100%' }}>
                                            <Title
                                                level={5}
                                                style={{
                                                    margin: 0,
                                                    fontSize: '14px',
                                                    lineHeight: '1.4',
                                                    minHeight: '40px'
                                                }}
                                                ellipsis={{ rows: 2 }}
                                            >
                                                {cluster.root_breaking_headline || cluster.cluster_headline}
                                            </Title>
                                            <Space split={<span>•</span>}>
                                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                                    {cluster.website_name}
                                                </Text>
                                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                                    {dayjs(cluster.root_pubdate).fromNow()}
                                                </Text>
                                            </Space>
                                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                                {cluster.num_related_articles} article{cluster.num_related_articles !== 1 ? 's' : ''}
                                            </Text>
                                        </Space>
                                    </Card>
                                ))}
                            </div>
                        </Card>
                    )}
                </Space>
            </Card>

            <Button
                onClick={handleBack}
                style={{
                    position: 'fixed',
                    right: '40px',
                    bottom: '70px',
                    zIndex: 1000
                }}
            >
                ← Back
            </Button>
        </div>
    );
};

export default ClusterDetail; 